import { Component, Vue } from "vue-property-decorator";
import WithRender from "./ActiveSendersTimelineDetails.html";
import { Inject } from "vue-di-container";
import RequestsSendersService from "../RequestsSendersService";
import { Toaster } from "@/Services/toast";
import { DatePicker } from "v-calendar";
import { SendersDetailsFilterModel } from "../SendersDetailsFilterModel";
import {
    assignActiveSendersTimelineRowKeys,
    buildActiveSendersScopePayload,
    downloadExcelFromBase64,
    EMPTY_GUID,
    formatCreatedDate,
    formatSenderExpiryDate,
    getTodayStr,
    loadSendersFilterFromStorage,
} from "../sendersDetailsShared";

@WithRender
@Component({
    components: {
        VcDatePicker: DatePicker,
    },
    diProvide: [RequestsSendersService],
})
export default class ActiveSendersTimelineDetails extends Vue {
    @Inject(RequestsSendersService)
    public requestsSendersService!: RequestsSendersService;

    public isLoading: boolean = false;
    public rows: any[] = [];
    public totalCount: number = 0;
    public clientItems: string[] = [];
    public senderNameFilter: string = "";
    public clientNameFilter: string = "";
    public senderExpiryDateRangeInternal: any = {
        start: "",
        end: "",
    };
    public senderExpiryDateFilterApplied: boolean = false;
    public maxSenderExpiryDate: string = "";
    public readonly launchDate: string = "2022-02-01";

    public senderExpiryDateModelConfig: any = {
        type: "string",
        mask: "YYYY-MM-DD",
    };

    public pagination: any = {
        page: 1,
        rowsPerPage: 10,
        sortBy: "createdDate",
        descending: true,
    };
    public isSorted: boolean = true;
    public headers: any[] = [];

    private sendersFilter: SendersDetailsFilterModel = {
        dateFrom: "2022-02-01",
        dateTo: "",
        tenantId: EMPTY_GUID,
        providerId: EMPTY_GUID,
        customerType: 0,
        activeNow: true,
    };

    private searchDebounceId: any = null;

    public clientNameMenuProps: Record<string, unknown> = {
        contentClass: "monitor-dashboard-select-menu all-requests-details-client-menu",
        offsetY: true,
        maxHeight: 260,
    };

    public get todayStr(): string {
        return getTodayStr();
    }

    public get senderExpiryDateRangeText(): string {
        if (!this.senderExpiryDateFilterApplied) {
            return "";
        }

        const from =
            this.senderExpiryDateRangeInternal &&
            this.senderExpiryDateRangeInternal.start
                ? this.senderExpiryDateRangeInternal.start
                : "";
        const to =
            this.senderExpiryDateRangeInternal &&
            this.senderExpiryDateRangeInternal.end
                ? this.senderExpiryDateRangeInternal.end
                : "";

        if (from && to) {
            return from + " - " + to;
        }
        if (from) {
            return from;
        }
        return "";
    }

    public created(): void {
        this.initHeaders();
        loadSendersFilterFromStorage(this.sendersFilter);
        this.applyRouteActiveNowFilter();
    }

    public async mounted(): Promise<void> {
        if (!this.sendersFilter.dateTo) {
            this.sendersFilter.dateTo = getTodayStr();
        }
        await this.loadMaxSenderExpiryDate();
        this.applyDefaultSenderExpiryDateRange();
        await this.loadClients();
        await this.loadTable();
    }

    public initHeaders(): void {
        this.headers = [
            {
                text: this.$t("monitorDashboard.requestId"),
                value: "requestId",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.createdDate"),
                value: "createdDate",
                align: "center",
                sortable: true,
                width: "160",
            },
            {
                text: this.$t("monitorDashboard.customerType"),
                value: "customerType",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderName"),
                value: "senderName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.clientName"),
                value: "clientName",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.senderExpiryDate"),
                value: "senderExpiryDate",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.smsServiceProvider"),
                value: "smsServiceProvider",
                align: "center",
                sortable: false,
            },
            {
                text: this.$t("monitorDashboard.serviceProvider"),
                value: "serviceProvider",
                align: "center",
                sortable: false,
            },
        ];
    }

    private applyRouteActiveNowFilter(): void {
        const q = this.$route.query;
        if (q && q.activeNow != null && (String(q.activeNow) === "1" || String(q.activeNow) === "true")) {
            this.sendersFilter.activeNow = true;
        }
    }

    private buildDetailsPayload(): any {
        const payload: any = buildActiveSendersScopePayload(this.sendersFilter);
        payload.senderName = this.senderNameFilter ? this.senderNameFilter.trim() : "";
        payload.clientName = this.clientNameFilter ? this.clientNameFilter.trim() : "";
        payload.pageIndex = (this.pagination.page - 1) >= 0 ? this.pagination.page - 1 : 0;
        payload.pageSize = this.pagination.rowsPerPage || 10;
        payload.sortBy = "CreatedDate";
        payload.sortDirection = this.pagination.descending === true;

        const expiryFrom = this.senderExpiryDateFilterApplied
            ? (this.senderExpiryDateRangeInternal &&
                this.senderExpiryDateRangeInternal.start
                ? this.senderExpiryDateRangeInternal.start
                : "")
            : this.launchDate;
        const expiryTo = this.senderExpiryDateFilterApplied
            ? (this.senderExpiryDateRangeInternal &&
                this.senderExpiryDateRangeInternal.end
                ? this.senderExpiryDateRangeInternal.end
                : "")
            : (this.maxSenderExpiryDate || this.launchDate);

        if (expiryFrom) {
            payload.senderExpiryDateFrom = expiryFrom;
        }
        if (expiryTo) {
            payload.senderExpiryDateTo = expiryTo;
        }

        return payload;
    }

    public async loadClients(): Promise<void> {
        const resp = await this.requestsSendersService.GetActiveSendersTimelineDetailsClients(
            buildActiveSendersScopePayload(this.sendersFilter),
        );
        if (!resp || !resp.isSuccess || !resp.result) {
            this.clientItems = [];
            return;
        }
        this.clientItems = resp.result;
    }

    public async loadTable(): Promise<void> {
        this.isLoading = true;
        const resp = await this.requestsSendersService.GetActiveSendersTimelineDetails(
            this.buildDetailsPayload(),
        );
        this.isLoading = false;

        if (!resp || !resp.isSuccess) {
            if (resp && resp.errorMessage) {
                Toaster.error(resp.errorMessage);
            }
            this.rows = [];
            this.totalCount = 0;
            return;
        }

        this.rows = assignActiveSendersTimelineRowKeys(resp.result || []);
        this.totalCount = resp.totalCount != null ? Number(resp.totalCount) : 0;
    }

    public onSenderNameInput(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onClientNameChange(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
            this.searchDebounceId = null;
        }
        this.pagination.page = 1;
        this.loadTable();
    }

    public onClientNameKeyup(): void {
        if (this.searchDebounceId) {
            clearTimeout(this.searchDebounceId);
        }
        const self = this;
        this.searchDebounceId = setTimeout(function () {
            self.pagination.page = 1;
            self.loadTable();
        }, 800);
    }

    public onSenderExpiryDateChange(): void {
        const from =
            this.senderExpiryDateRangeInternal &&
            this.senderExpiryDateRangeInternal.start
                ? this.senderExpiryDateRangeInternal.start
                : "";
        const to =
            this.senderExpiryDateRangeInternal &&
            this.senderExpiryDateRangeInternal.end
                ? this.senderExpiryDateRangeInternal.end
                : "";

        this.senderExpiryDateFilterApplied = !!(from || to);
        this.pagination.page = 1;
        this.loadTable();
    }

    public async loadMaxSenderExpiryDate(): Promise<void> {
        const resp = await this.requestsSendersService.GetActiveSendersTimelineDetailsMaxSenderExpiryDate(
            buildActiveSendersScopePayload(this.sendersFilter),
        );
        if (resp && resp.isSuccess && resp.result) {
            this.maxSenderExpiryDate = String(resp.result);
            return;
        }
        this.maxSenderExpiryDate = this.launchDate;
    }

    private applyDefaultSenderExpiryDateRange(): void {
        this.senderExpiryDateRangeInternal = {
            start: "",
            end: "",
        };
        this.senderExpiryDateFilterApplied = false;
    }

    public async resetLocalFilters(): Promise<void> {
        this.senderNameFilter = "";
        this.clientNameFilter = "";
        this.applyDefaultSenderExpiryDateRange();
        this.pagination.page = 1;
        await this.loadTable();
    }

    public async onExport(): Promise<void> {
        const data = await this.requestsSendersService.ExportActiveSendersTimelineDetails(
            this.buildDetailsPayload(),
        );
        if (!data || !data.isSuccess || !data.result) {
            if (data && data.errorMessage) {
                Toaster.error(data.errorMessage);
            }
            return;
        }
        downloadExcelFromBase64(data.result, "ActiveSendersTimelineDetails.xlsx");
    }

    public goBack(): void {
        this.$router.push({
            name: "MonitorDashboard",
            query: {
                topTab: "0",
                requestsTab: "1",
            },
        });
    }

    public changeSort(column: string): void {
        if (column !== "createdDate") {
            return;
        }
        if (this.pagination.sortBy === column) {
            this.pagination.descending = !this.pagination.descending;
        } else {
            this.pagination.sortBy = column;
            this.pagination.descending = true;
        }
        this.isSorted = true;
        this.loadTable();
    }

    public async updatePaginate(val: any): Promise<void> {
        this.pagination.page = val.page;
        this.pagination.rowsPerPage = val.rowsPerPage;
        await this.loadTable();
    }

    public formatCreatedDate(value: string): string {
        return formatCreatedDate(value);
    }

    public formatSenderExpiryDate(value: string): string {
        return formatSenderExpiryDate(value);
    }
}
